import { useApi } from "@/hooks/use-api";
import { formatDateLocal } from "@/lib/dates";
import { useAppState } from "@/store/useApp";
import { AssessmentInfo } from "@/types/assessment";
import { Course } from "@/types/course";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PublishAssessmentDialog } from "./assessments/PublishAssessmentDialog";
import { StudentPortalAssessments } from "./student-portal/StudentPortalAssessments";
import {
  StudentPortalAssessment,
  StudentPortalLane,
} from "./student-portal/types";

interface StudentPortalProps {
  course: Course;
  studentCount: number;
  onShare: () => void;
}

export const StudentPortal = ({
  course,
  studentCount,
  onShare,
}: StudentPortalProps) => {
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishDialogMode, setPublishDialogMode] = useState<"publish" | "edit">(
    "publish"
  );
  const [editingAssessment, setEditingAssessment] =
    useState<AssessmentInfo | null>(null);
  const assessments = useAppState((state) => state.assessments);
  const documents = useAppState((state) => state.documents);
  const api = useApi();

  const hasPendingAssessments = useMemo(() => {
    return (
      assessments?.some(
        (a) => a.courseId === course.id && a.status === "pending"
      ) ?? false
    );
  }, [assessments, course.id]);

  const assessmentsWithStats = useMemo<StudentPortalAssessment[]>(() => {
    if (!assessments) return [];

    return assessments
      .filter((a) => a.courseId === course.id)
      .map((assessment) => {
        const assessmentDocs =
          documents?.filter((d) => d.assessmentId === assessment.id) || [];

        const lateCount = assessmentDocs.filter((d) => {
          if (!assessment.scheduledUntil) return false;
          const deadline = new Date(assessment.scheduledUntil);
          const submitted = new Date(d.createdAt);
          return submitted > deadline;
        }).length;

        return {
          ...assessment,
          stats: {
            submissions: assessment.submittedCount ?? 0,
            late: lateCount,
            graded: assessment.gradedCount ?? 0,
          },
        };
      });
  }, [assessments, documents, course.id]);

  const getLane = (assessment: StudentPortalAssessment): StudentPortalLane | null => {
    if (!assessment.status || assessment.status === "pending") return null;
    if (assessment.status === "review") return "review";
    if (assessment.status === "completed") return "completed";

    const scheduledFrom = assessment.scheduledFrom
      ? new Date(assessment.scheduledFrom)
      : null;
    const isFuture = !!scheduledFrom && scheduledFrom > new Date();
    return isFuture ? "scheduled" : "ongoing";
  };

  const laneAssessments = useMemo(
    () =>
      assessmentsWithStats.reduce<Record<StudentPortalLane, StudentPortalAssessment[]>>(
        (acc, assessment) => {
          const lane = getLane(assessment);
          if (!lane) return acc;
          acc[lane].push(assessment);
          return acc;
        },
        { scheduled: [], ongoing: [], review: [], completed: [] }
      ),
    [assessmentsWithStats]
  );

  const handleToggleGrades = async (assessmentId: string, feedbackLive: boolean) => {
    try {
      await api.patchAssessment(course.id, assessmentId, {
        feedbackLive,
      });
    } catch (error) {
      console.error("Failed to toggle grades:", error);
    }
  };

  const handleMoveAssessment = async (
    assessmentId: string,
    lane: StudentPortalLane
  ) => {
    const assessment = assessmentsWithStats.find((item) => item.id === assessmentId);
    if (!assessment) return;
    const sourceLane = getLane(assessment);

    const status =
      lane === "review"
        ? "review"
        : lane === "completed"
          ? "completed"
          : "active";

    const formatDateToLocalString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      const shouldSetToday = sourceLane === "scheduled" && lane === "ongoing";
      const shouldRescheduleNextWeek =
        sourceLane === "ongoing" && lane === "scheduled";
      const nextWeekDate = new Date();
      nextWeekDate.setDate(nextWeekDate.getDate() + 7);
      const nextWeekDeadline = assessment.scheduledUntil
        ? new Date(assessment.scheduledUntil)
        : null;

      if (nextWeekDeadline) {
        nextWeekDeadline.setDate(nextWeekDeadline.getDate() + 7);
      }

      await api.patchAssessment(course.id, assessmentId, {
        status,
        ...(shouldSetToday
          ? { scheduledFrom: formatDateToLocalString(new Date()) }
          : {}),
        ...(shouldRescheduleNextWeek
          ? {
              scheduledFrom: formatDateToLocalString(nextWeekDate),
              ...(nextWeekDeadline
                ? { scheduledUntil: formatDateToLocalString(nextWeekDeadline) }
                : {}),
            }
          : {}),
      });

      if (shouldRescheduleNextWeek) {
        toast.success(
          `Assessment rescheduled for ${formatDateLocal(nextWeekDate, {
            timeStyle: undefined,
            dateStyle: "long",
          })}`
        );
      }
    } catch (error) {
      console.error("Failed to move assessment:", error);
    }
  };

  return (
    <>
      <StudentPortalAssessments
        course={course}
        studentCount={studentCount}
        shortUrl={course.shortUrl}
        laneAssessments={laneAssessments}
        canPublish={hasPendingAssessments}
        onUploadAssessment={() => {
          setPublishDialogMode("publish");
          setEditingAssessment(null);
          setPublishDialogOpen(true);
        }}
        onMoveAssessment={handleMoveAssessment}
        onEdit={(assessment) => {
          setPublishDialogMode("edit");
          setEditingAssessment(assessment);
          setPublishDialogOpen(true);
        }}
        onToggleGrades={handleToggleGrades}
        onShare={onShare}
      />

      <PublishAssessmentDialog
        courseId={course.id}
        open={publishDialogOpen}
        onOpenChange={(open) => {
          setPublishDialogOpen(open);
          if (!open) setEditingAssessment(null);
        }}
        assessment={editingAssessment || undefined}
        disableAssessmentSelect={!!editingAssessment}
        mode={publishDialogMode}
      />
    </>
  );
};
